import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";

import { fetchNameConflicts, uploadAssets } from "reducers/Assets";
import { openModal } from "reducers/Modal";
import { RESOLUTIONS } from "hooks/useConflictResolver";
import { SUPPRESS_SUCCESS_GROWL, ASSET_MAX_BYTES_BY_TYPE, MODAL_TYPES } from "utils/constants";
import { formatBytes, getFilenames } from "utils/common";
import { isAfterEffectsAsset } from "utils/Assets";
import ValidatingAssetsMessage from "widgets/messages/ValidatingAssetsMessage";
import UploadListMessage from "widgets/messages/UploadListMessage";
import useNotifications from "hooks/useNotifications";
import useFTMessageGrowl from "hooks/useFTMessageGrowl";
import UploadErrorModal from "widgets/modals/assets/BulkUpload/UploadErrorModal";

export const AssetUploadContext = React.createContext();

function generateFormData(files, resolutions, key="files"){
	return files.reduce((prev, file) => {
		const conflictResolution = resolutions[file.name] || {};

		if(conflictResolution.resolution === RESOLUTIONS.RENAME && conflictResolution.newName){
			prev.append(key, file, conflictResolution.newName);
		} else {
			// This is assumed to be an overwrite
			prev.append(key, file);
		}
		return prev;
	}, new FormData());
}

const UPLOAD_BATCH_TOPIC = "Upload Batch";
const UPLOAD_ASSET_TASK = "Upload Assets";

function uploadBatchNotifications(detail){
	let { task, status, data } = detail?.message || {};
	if((task === UPLOAD_ASSET_TASK) && (status === "Success")){
		let numFiles = data?.files?.length;
		let assetType = data?.assetType || "asset";
		if(numFiles){ // This should always happen, but just in case ...
			let message = `Successfully uploaded ${numFiles} ${assetType}${numFiles > 1 ? "s" : ""}`;
			return {
				type: "success",
				messageHeader: "Upload Successful",
				message,
			};
		}
	}
}

function useUploadQueue(){
	/*
		This manages dispatching uploads, and adding messages to the Notifications panel.

		`enqueueBatch` is used to add a group of files to the queue of batches.

		Batches are processed one at a time, and one file at a time.
			- Uploading many at once made it impossible to track individual errors, upload progress,
			  and would make whole batches fail if there was a problem with one file
			- We don't parallelize because it could produce too many requests at a time from one client
			  and overload the API
	*/
	const {
		addMessage,
		transformMessage,
	} = useNotifications();
	const dispatch = useDispatch();

	useFTMessageGrowl([uploadBatchNotifications]);

	const [batchFiles, setBatchFiles] = useState([]); // Files for the current batch
	const [batches, setBatches] = useState([]);

	const filesHead = batchFiles[0] || null;
	const batchHead = batches[0] || null;

	function enqueueBatch(batch){
		/*
			This is used to add a group of files to the queue of batches.
			`batch` is expected to be an object in the form:
			{
				assetType,
				libraryName,
				files: [
					{
						assetType,
						libraryId,
						formData,
						filename,
					}, ...
				]
			}
		*/
		let {
			libraryId,
			libraryName,
			assetType,
			files,
		} = batch;
		let numFiles = files.length;
		let batchId = Date.now();

		setBatches([...batches, {
			...batch,
			batchId,
		}]);

		addMessage(UPLOAD_BATCH_TOPIC, {
			id: batchId,
			jobId: batchId,
			origin: "HOPI",
			task: UPLOAD_ASSET_TASK,
			status: "In Progress",
			data: {
				assetType,
				libraryId,
				libraryName,
				header: `Uploading ${numFiles} ${assetType} asset${(numFiles > 1) ? "s" : ""}`,
				files,
			},
			component: UploadListMessage,
		});
	}

	function dequeueFile(){
		setBatchFiles(batchFiles.slice(1));
	}

	function addErrorMessage(filename, error){
		transformMessage(UPLOAD_BATCH_TOPIC, batchHead.batchId, (message) => {
			let messageData = message?.data || {};
			let messageErrors = messageData.errors || {};

			let updatedErrors = {
				...messageErrors,
				[filename]: error,
			};

			return {
				...message,
				data: {
					...messageData,
					errors: updatedErrors,
				},
			};
		});
	}

	useEffect(() => {
		// When the head batch changes, we have to repopulate batchFiles with its files.
		if(!filesHead && batchHead?.files){
			setBatchFiles(batchHead.files);
		}
	}, [batchHead]);

	useEffect(() => {
		if((batchFiles.length === 0) && batchHead){
			// We've emptied the batch's files, and can go to the next batch

			let { assetType, files } = batchHead;
			let numFiles = files.length;

			// Finalize the Notifications panel message about the batch
			transformMessage(UPLOAD_BATCH_TOPIC, batchHead.batchId, (message) => {
				let status = "Success";
				let header = `Uploaded ${numFiles} ${assetType} asset${(numFiles > 1) ? "s" : ""}`;

				let errors = message.data?.errors || {};
				let numErrors = Object.keys(errors).length;

				if(numErrors === numFiles){
					status = "Failed";
					header = `${numErrors} ${assetType} upload${(numFiles > 1) ? "s" : ""} failed`;
				} else if(numErrors > 0){
					status = "warn";
					header = `${numErrors} of ${numFiles} ${assetType} upload${(numFiles > 1) ? "s" : ""} failed`;
				}

				return {
					...message,
					status,
					data: {
						...(message?.data || {}),
						header,
					},
				};
			});

			setBatches(batches.slice(1));
		}
	}, [batchFiles]);

	useEffect(() => {
		if(!filesHead){
			return;
		}

		let {
			assetType,
			libraryId,
			formData,
			filename,
			size,
		} = filesHead;

		let maxSize = ASSET_MAX_BYTES_BY_TYPE[assetType];
		if(size > maxSize){
			/*
				These files aren't uploaded, but we want them added to the list in the Notifications panel
				so we can display the associated error, which is why they aren't filtered out earlier with
				the files that the user chose to skip.
			*/
			addErrorMessage(filename, `File size exceeds ${formatBytes(maxSize)}`);
			dequeueFile();
			return;
		}

		// Validate if the asset has a file extension and add the error in the notification panel
		if (!filename.includes(".")) {
			addErrorMessage(filename, "File requires a file extension");
			dequeueFile();
			return;
		}

		function updateProgress(progressEvent){
			let { batchId } = batchHead;
			let { loaded, total } = progressEvent;

			function updateMessageProgress(message){
				let messageData = message?.data || {};
				let { progress = {} } = messageData;
				return {
					...message,
					data: {
						...messageData,
						progress: {
							...progress,
							[filename]: {
								loaded,
								total,
							},
						},
					},
				};
			}
			transformMessage(UPLOAD_BATCH_TOPIC, batchId, updateMessageProgress);
		}

		dispatch(uploadAssets(assetType, libraryId, formData, {
			onUploadProgress: updateProgress,
			meta: SUPPRESS_SUCCESS_GROWL,
		})).then((result) => {
			if(!result.success){
				/*
					After Effects archives may get an error response because of problems in the archive.
					When this happens, we expect a 400 error response, with an `errors` array, which
					we forward to a modal to present to the user.
				*/
				const errors = result?.payload?.errorObj?.errors;
				if(errors && isAfterEffectsAsset(assetType)){
					const filename = result?.payload?.errorObj?.filename;
					dispatch(openModal(MODAL_TYPES.AE_UPLOAD_ERRORS, { errors, filename }));
				} else {
					let error = result?.payload?.errorObj?.description || result.payload?.error?.message || "Upload error"; /*
						Generally, the `errorObj.description` has the message that we want, but when we get a 504 gateway timeout,
						`errorObj` is just an empty string, so we fall back to `error.message`, which is better than nothing.
					*/
					addErrorMessage(filename, error);
				}
			} else {
				/**
				 * Even if the response is successful, the file may have errored out. Since files are uploaded sequentially,
				 * we can find this in the 0 index of the data object. This has only been encountered on audio files so far.
				 * HOPI-1314
				 */
				const errors = result.payload.data?.[0]?.errors;
				if (errors) {
					errors.forEach((errorObj) => {
						addErrorMessage(filename, errorObj.error);
					});
				}
			}

			dequeueFile();
		});
	}, [filesHead]);

	return enqueueBatch;
}

export function AssetUploadController({ children }){
	/*
		This component will manage the logic for processing a bulk asset upload, which will involve
		producing FormData to send to the asset type's upload-many endpoint.

		Components that subscribe to the `AssetUploadContext` can call the `stageUpload` function, provided
		in the context's `value`, providing the asset type, the library ID, and a FileList object, provided
		from a file input.  Calling `stageUpload` will kick off the upload process:

			- It will initially check for conflicting asset names that exist in the library already,
			and then prompt the user for how to resolve the conflicts, if they exist.
				- If they don't exist, we'll simply upload the files without any intervention from the user.

		Users will have the option to skip, overwrite or rename any conflicted files.

		The general strategy will be to produce a single request with a single FormData object, handling
		all conflict resolution.
			- Skipped items will be removed from the list on the client side
			- Renamed items will be `append`ed to the FormData with the optional third filename argument.
			- Items to overwrite will be resolved by using the `overwrite` flag on the FormData.


		e.g. If we have conflicted files named as follows, with the user-specified resolutions as commented:
		```
			fileList: [
				"video1.mp4", // skip
				"video2.mp4", // overwrite
				"video3.mp4", // rename to "video3v2.mp4"
			]
		```

		We will produce FormData like:
		```
			let formData = new FormData();
			formData.append("overwrite", true); // Tell upload-many to overwrite existing assets with name conflicts

			// fileList[0] (video1.mp4) isn't added to `formData`

			formData.append("files", fileList[1]); // video2.mp4 is added and will overwrite

			// rename video3.mp4 to video3v2.mp4 and leave video3.mp4 in place
			formData.append("files", fileList[2], "video3v2.mp4");
		```
	*/
	const dispatch = useDispatch();

	const [assetType, setAssetType] = useState(null);
	const [libraryId, setLibraryId] = useState(null);
	const [libraryName, setLibraryName] = useState("Library");
	const [fileList, setFileList] = useState(null);
	const [conflicts, setConflicts] = useState([]);

	const [isValidating, setIsValidating] = useState(false);

	const {
		addMessage,
		transformMessage,
		readMessage,
	} = useNotifications();

	function resetState(){
		setAssetType(null);
		setLibraryId(null);
		setLibraryName("Library");
		setFileList(null);
		setConflicts([]);
		setIsValidating(false);
	}

	const filenames = useMemo(() => getFilenames(fileList), [fileList]);
	const namesInUse = useMemo(() => {
		/*
			We need to pass the names that aren't conflicted to the UploadErrorModal so that
			they're not allowed to be used when renaming the conflicted files.
		*/
		let nameSet = new Set(filenames);

		conflicts.forEach((conflict) => {
			let { name } = conflict;
			nameSet.delete(name);
		});

		return [...nameSet];
	}, [filenames, conflicts]);

	let pluralizedType = `${assetType}s`;

	function stageUpload(targetAssetType, targetLibraryId, targetLibraryName, newFileList){
		setAssetType(targetAssetType);
		setLibraryId(targetLibraryId);
		setLibraryName(targetLibraryName);
		setFileList(newFileList);
		setConflicts([]);
	}

	let enqueueBatch = useUploadQueue();

	useEffect(async () => {
		if(!assetType || !libraryId || !fileList){
			return;
		}

		let pluralizedType = `${assetType}s`;

		let message = {
			origin: "HOPI",
			task: "Validating Upload",
			status: "In Progress",
			data: {
				libraryName,
				assetType,
				numFiles: fileList.length,
			},
			component: ValidatingAssetsMessage,
		};
		let validatingMessageId = addMessage("Validating Upload", message);
		setIsValidating(true);

		try{
			let newConflicts = await dispatch(fetchNameConflicts(libraryId, pluralizedType, filenames));

			readMessage(validatingMessageId);
			setIsValidating(false);

			if(newConflicts.length){
				setConflicts(newConflicts);
			} else {
				// There are no conflicts, safe to upload
				startUpload();
			}
		} catch(validationError){
			// There was a problem with the conflicts API check
			transformMessage("Validating Upload", validatingMessageId, (message) => {
				return {
					...message,
					status: "Failed",
				};
			});
			resetState();
		}
	}, [assetType, libraryId, fileList]);


	function startUpload({
		resolutions = {},
		shouldOverwrite = false,
	} = {}){
		let fileArray = [...(fileList || [])]; // FileList doesn't have normal array operations, so we convert it

		// Filter out files that the user chose to skip
		let filesToUpload = fileArray.filter(file => resolutions[file.name]?.resolution !== RESOLUTIONS.SKIP);

		if(filesToUpload.length === 0){
			resetState();
			return;
		}

		let batchFiles = filesToUpload.map((file) => {
			const isAfterEffects = isAfterEffectsAsset(assetType);
			const key = isAfterEffects ? "file" : "files";
			let formData = generateFormData([file], resolutions, key);
			let { resolution, newName } = resolutions[file.name] || {};
			let filename = ((resolution === RESOLUTIONS.RENAME) && newName) ? newName : file.name;
			if(shouldOverwrite){
				formData.append(isAfterEffects ? "overwriteAssets" : "overwrite", true); /*
					By this point, we've filtered out any files to skip, and renamed files in the FormData,
					so any conflicts left have been cleared by the user to overwrite.
				*/
			} else {
				formData.append("skip", true);
			}

			return {
				assetType,
				libraryId,
				formData,
				filename,
				size: file.size,
			};
		});

		enqueueBatch({
			assetType,
			libraryId,
			libraryName,
			files: batchFiles,
		});

		resetState();
	}

	const modalProps = {
		assetType: pluralizedType,
		libraryId,
		conflicts,
		namesInUse,

		onCancel: resetState,
		onCommit: startUpload,
	};

	const value = {
		stageUpload,
		isValidating, // When validating, we should disable any upload asset buttons
	};

	return (
		<>
			<AssetUploadContext.Provider value={value}>
				{children}
			</AssetUploadContext.Provider>
			{ conflicts?.length ? <UploadErrorModal {...modalProps} /> : null }
		</>
	);
}
